import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { Platform, AppState } from 'react-native';
import { Session, User, AuthError } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import * as WebBrowser from 'expo-web-browser';
import { makeRedirectUri } from 'expo-auth-session';
import { supabase, isSupabaseConfigured } from './client';
import { rpc } from './rpc';
import { useInverioStore } from '@/lib/state/inverio-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { useScoreHistoryStore } from '@/lib/state/score-history-store';
import { track, identify, resetAnalytics, setAnalyticsConsent } from '@/lib/analytics';
import { reportError, setSentryUser } from '@/lib/errors';
import { isBetaMode, deactivateDemoBypass, mode } from '@/lib/config/app-mode';
import { isWeb } from '@/lib/platform';
import { clearGoogleTokens } from '@/lib/google-drive/auth';
import { clearDropboxTokens } from '@/lib/dropbox/auth';
import { shouldTriggerEnrichment, resetEnrichmentGuard } from './enrichment-guard';
import { mapStartupProfileRow, mapInvestorProfileRow } from './profile-mappers';
import { asAuthUserId, type AuthUserId } from '@/lib/ids';
import { loadMyStartupProfileRow } from '@/lib/startup/my-startup-profile';
import { deriveDisplayName } from '@/lib/profile/display-name';
import type { Profile, UserRole } from './types';

// Complete any pending auth session (needed for mobile OAuth redirect)
WebBrowser.maybeCompleteAuthSession();

// Edge function URL for MFA operations
const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL || '';

/**
 * Ceiling on the awaited global sign-out revocation (SEC-AUTH-05).
 *
 * Sized against the same evidence as the sign-in ceiling documented in
 * `signIn` below: the GoTrue /logout round-trip completes in well under a
 * second on a healthy connection, and the existing sign-in wrapper uses 8s to
 * cover genuinely slow mobile networks. 5s sits inside that envelope — long
 * enough that a slow-but-working network still CONFIRMS revocation (the
 * outcome we actually want), short enough that a dead network does not hold a
 * user on a logout spinner. Exceeding it does not abort the sign-out; it only
 * means we report the revocation as unconfirmed.
 */
const SIGN_OUT_REVOCATION_TIMEOUT_MS = 5000;

/**
 * How long a genuine `PASSWORD_RECOVERY` event keeps the reset-password screen
 * unlocked (SEC-AUTH-03).
 *
 * The window exists because recovery is a CAPABILITY, not a session property —
 * it must expire on its own so a recovery link opened on a shared machine and
 * then walked away from stops being a live "change this account's password"
 * button. 15 minutes is comfortably longer than the seconds it takes to type a
 * new password twice, and far shorter than the recovery token's own server-side
 * lifetime, so this is the binding constraint rather than a formality.
 *
 * Deliberately NOT persisted: the flag lives in memory only, so a page reload
 * or an app relaunch closes the window. That is the point — an attacker who
 * finds the tab later cannot reopen it, and a legitimate user who reloads
 * simply requests a fresh link.
 */
const PASSWORD_RECOVERY_WINDOW_MS = 15 * 60 * 1000;

// Helper with timeout.
//
// The timer is CLEARED once the race settles. Without that, every call left a
// live timer running for its full duration even when the underlying promise
// resolved immediately — harmless in production but real: it keeps the JS
// runtime awake, and under Jest it holds the worker open past the test run.
const withTimeoutHelper = <T,>(promise: Promise<T>, ms: number, errorMsg: string): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      timer = setTimeout(() => reject(new Error(errorMsg)), ms);
    }),
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

// Helper to call MFA edge function using SDK session for auth (works on both web and native)
const callMfaEdgeFunction = async (
  action: string,
  body: Record<string, any>
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return { success: false, error: 'No active session for MFA' };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    const response = await fetch(`${SUPABASE_URL}/functions/v1/mfa-verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '',
      },
      body: JSON.stringify({ action, ...body }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || `HTTP ${response.status}` };
    }

    if (data.error) {
      return { success: false, error: data.error };
    }

    return { success: true, data };
  } catch (error: any) {
    reportError(error, { context: 'callMfaEdgeFunction' });
    if (error.name === 'AbortError') {
      return { success: false, error: 'Request timeout' };
    }
    return { success: false, error: error.message || 'Failed to call edge function' };
  }
};

/**
 * Tear down the AAL1 session that `signInWithPassword` just created, when MFA
 * could not be initialised (SEC-AUTH-02b).
 *
 * WHY THIS IS NECESSARY. Password verification and MFA are two separate round
 * trips, and the FIRST one already mints a real session: supabase-js persists
 * the access + refresh token to storage before `signIn` ever looks at MFA. So
 * between those two steps the user holds a fully-functional AAL1 credential —
 * PostgREST accepts it, edge functions accept it, and it auto-refreshes.
 *
 * Returning `{ error }` (the existing fail-closed behaviour) only prevents the
 * UI from navigating. It does not revoke anything. An attacker who can make
 * `listFactors` or `mfa.challenge` fail — a network position, or simply a
 * Supabase blip — can therefore close the app, reopen it, and be signed in at
 * AAL1 with MFA never presented. Refusing to advance a screen is not an
 * authorization boundary; destroying the credential is.
 *
 * Uses `scope: 'local'` deliberately. The user has proven the password, so this
 * is not a compromise signal — it is an abandoned login. A global sign-out here
 * would kill that user's OTHER legitimate devices every time this flaky lookup
 * failed, which is a denial-of-service on our own users. Local removes the
 * credential this attempt created, which is exactly the one that must not
 * survive.
 *
 * Never throws: this runs on an error path, and a failure to clean up must not
 * mask the original MFA error the caller is about to report.
 */
const discardProvisionalSession = async (context: string): Promise<void> => {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (err) {
    // NOT silent. `silent: true` skips Sentry entirely (errors.ts:207), and
    // this is the one failure we must be able to see remotely: the comment
    // above is explicit that destroying the credential — not refusing a
    // screen — is the authorization boundary. A failed discard leaves a
    // provisional AAL1 session alive with MFA never presented, and we would
    // have had no signal that it happened.
    //
    // Still never throws: the contract above is deliberate, so the original
    // MFA error the caller is about to report is not masked by cleanup noise.
    reportError(err, { context: `${context}.discardProvisionalSession`, level: 'warning' });
  }
};

// Legacy helper for compatibility
const callMfaFunction = async (
  action: 'verify' | 'unenroll' | 'enroll-verify',
  factorId: string,
  code: string
): Promise<{ success: boolean; error?: string }> => {
  return callMfaEdgeFunction(action, { factorId, code });
};

interface AuthState {
  session: Session | null;
  /**
   * The signed-in user, whose `id` is branded `AuthUserId`.
   *
   * Branding at this ORIGIN rather than at every call site is what makes the
   * id-space split enforceable without a 97-site rewrite: `authUser?.id` is
   * now an AuthUserId everywhere, so passing it where an
   * `investor_profiles(id)` is expected (or vice versa) is a build error
   * rather than a silently-empty query. See src/lib/ids.ts.
   */
  user: (User & { id: AuthUserId }) | null;
  profile: Profile | null;
  isLoading: boolean;
  isConfigured: boolean;
  /**
   * True ONLY between a genuine Supabase `PASSWORD_RECOVERY` auth event and the
   * end of the recovery window (success, sign-out, or timeout).
   *
   * This is the authorization state the reset-password screen gates on. It is
   * deliberately NOT derived from `session`: an ordinary authenticated session
   * is not a recovery session, and treating it as one let any signed-in user
   * change their password without the AAL2 step-up and current-password check
   * that change-password.tsx enforces.
   */
  isPasswordRecovery: boolean;
}

interface LinkedInData {
  isLinkedInAuth: boolean;
  linkedInUrl: string | null;
  linkedInId: string | null;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
}

interface MFAChallenge {
  factorId: string;
  challengeId: string;
}


/**
 * The one place a Supabase `User` becomes an id-space-typed user.
 *
 * Supabase returns `id: string`; every consumer downstream needs to know it is
 * an `auth.users(id)` and not an `investor_profiles(id)`. Asserting it once
 * here means `authUser?.id` is correctly typed app-wide. See src/lib/ids.ts.
 */
function brandSessionUser(session: Session | null): (User & { id: AuthUserId }) | null {
  return (session?.user as (User & { id: AuthUserId }) | undefined) ?? null;
}

interface AuthContextType extends AuthState {
  /**
   * True when the signed-in user's email is confirmed (`email_confirmed_at` is set).
   * SAFETY: when remote email confirmation is OFF, Supabase sets `email_confirmed_at`
   * at signup, so this is `true` for every existing session and the access gate stays
   * inert. Only a genuinely-null `email_confirmed_at` yields `false`. No user → `false`.
   */
  emailConfirmed: boolean;
  signUp: (email: string, password: string, role: UserRole, firstName?: string, lastName?: string) => Promise<{ error: AuthError | null; needsEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null; mfaRequired?: boolean; mfaChallenge?: MFAChallenge }>;
  signInWithLinkedIn: () => Promise<{ error: AuthError | null }>;
  /**
   * Sign out everywhere.
   *
   * Always completes local cleanup (stores, query cache, SecureStore OAuth
   * tokens, web localStorage) even when the network is down. Resolves once the
   * server-side global revocation has been attempted, with `remoteRevoked`
   * reporting whether the server CONFIRMED it — `false` means the local session
   * is gone but the refresh token may still be live server-side, so a caller
   * that wants to warn the user can. Never rejects, never blocks indefinitely.
   *
   * CAVEAT (see docs/security/auth-deployment-checklist.md): revocation
   * invalidates the REFRESH token. Access JWTs already issued stay
   * cryptographically valid until they expire unless Supabase is configured to
   * check the session server-side on each request.
   */
  signOut: () => Promise<{ remoteRevoked: boolean }>;
  refreshProfile: () => Promise<void>;
  getLinkedInData: () => LinkedInData;
  // Password recovery
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  /**
   * Close the password-recovery window immediately.
   *
   * Called by reset-password.tsx once the password has actually been changed,
   * so the capability is spent rather than left open for the remainder of the
   * window. Safe to call when no window is open.
   */
  endPasswordRecovery: () => void;
  // Email confirmation (access gate — onboarding redesign D2)
  resendConfirmationEmail: (email: string) => Promise<{ error: AuthError | null }>;
  changeEmail: (newEmail: string) => Promise<{ error: AuthError | null }>;
  // MFA
  enrollMFA: () => Promise<{ error: AuthError | null; qrCode?: string; secret?: string; factorId?: string }>;
  verifyMFAEnrollment: (factorId: string, code: string) => Promise<{ error: AuthError | null; success: boolean }>;
  verifyMFA: (factorId: string, challengeId: string, code: string) => Promise<{ error: AuthError | null }>;
  verifyActiveMFA: (factorId: string, code: string) => Promise<{ error: AuthError | null; success: boolean }>;
  unenrollMFA: (factorId: string, code?: string) => Promise<{ error: AuthError | null; success: boolean }>;
  getMFAFactors: () => Promise<{ factors: any[]; error: AuthError | null }>;
}

// Guard to prevent concurrent profile creation attempts (e.g., rapid LinkedIn OAuth events)
let profileCreationInProgress = false;

/**
 * Fire-and-forget call to enrich an investor profile via Proxycurl.
 * Fetches LinkedIn about, experience, skills and populates investor_profiles.
 * Non-blocking — failures are logged but don't affect the auth flow.
 */
function triggerLinkedInEnrichment(userId: string, linkedInUrl: string) {
  // Dedup per signed-in session; the guard is re-armed on sign-out (resetEnrichmentGuard).
  if (!shouldTriggerEnrichment()) return;

  // Use the authenticated user's session token, not the anon key
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (!session?.access_token) return;

    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL || '';
    if (!supabaseUrl) return;

    fetch(`${supabaseUrl}/functions/v1/enrich-linkedin-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ linkedin_url: linkedInUrl }),
    })
      .then(async (resp) => {
        if (resp.ok) {
          const result = await resp.json();
          if (result.success) {
            // Mark enrichment as done so we don't re-trigger
            supabase.from('profiles').update({
              onboarding_progress: {
                linkedin_enriched: true,
              },
            }).eq('id', userId).then(async () => {
              // Re-sync profile store with the real session to pick up enriched data
              const { data: { session: currentSession } } = await supabase.auth.getSession();
              if (!currentSession?.user) return;
              const { data: currentProfile } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', currentSession.user.id)
                .single();
              // Clear investor profile so syncUserToProfileStore re-fetches with enriched columns
              useProfileStore.setState({ investorProfile: null });
              syncUserToProfileStore(currentSession.user, currentProfile);
            });
          }
        }
      })
      .catch((err) => {
        reportError(err, { context: 'triggerLinkedInEnrichment', silent: true });
      });
  });
}

/**
 * Sync authenticated user data to the profile store.
 *
 * Called on initial session load and on every auth state change.
 * Populates user info (name, avatar) and role-specific profiles
 * (investor_profiles / startup_profiles) so dashboard components
 * have data immediately — without relying on login.tsx being mounted.
 */
async function syncUserToProfileStore(user: User, profile: Profile | null) {
  const store = useProfileStore.getState();

  // CLEAN-SLATE-ON-USER-CHANGE: persisted Zustand state (dealflow, scores,
  // company profile, etc.) is scoped to a single user, but nothing on the web
  // clears it when a DIFFERENT account signs in on the same browser (only
  // explicit sign-out runs the reset cascade). So a fresh account would inherit
  // the previous user's — or a demo session's — data ("residual mock data
  // everywhere"). If the store already belongs to a different user id, wipe
  // every persisted store before hydrating the new one. Same-user session
  // restore / token refresh is a no-op (ids match), so real data is preserved.
  const previousUserId = store.user?.id;
  if (previousUserId && previousUserId !== user.id) {
    useInverioStore.getState().reset();
  }

  const meta = user.user_metadata || {};

  // Set user identity. Display name is resolved from profiles.first_name/last_name
  // (the source of truth), falling back to user_metadata only for accounts with
  // no captured name — so an onboarded/edited name surfaces on reload. bio /
  // location / avatar likewise come from profiles (the persisted personal
  // fields); avatar falls back to the OAuth metadata avatar until one is uploaded.
  store.setUser({
    // Boundary: `user` is Supabase's own type, so its id is an unbranded
    // string here. This is the auth id by definition.
    id: asAuthUserId(user.id),
    name: deriveDisplayName(profile, user),
    email: user.email || '',
    avatarUrl: profile?.avatar_url || meta.avatar_url || meta.picture || undefined,
    bio: profile?.bio || undefined,
    location: profile?.location || undefined,
  });

  // Set role
  const role = profile?.role as 'startup' | 'investor' | undefined;
  if (role && role !== ('pending' as string)) {
    store.setUserRole(role);
    store.initializeOnboarding(role);
    if (profile?.onboarding_completed) {
      store.completeOnboarding();
    }

    // Sync the subscription store at login: set the role and load the plan from
    // the DB so feature gating reflects the user's real tier EVERYWHERE.
    // Previously loadSubscription() only ran on the subscription screen, so a
    // fresh login resolved to `null` (free tier) and gated paid features even
    // for users (e.g. demo accounts) who actually have an active subscription.
    const subStore = useSubscriptionStore.getState();
    subStore.setUserRole(role as 'startup' | 'investor' | 'admin');
    subStore.loadSubscription();
  }

  // Fetch and set role-specific profile
  if (role === 'investor' && !store.investorProfile) {
    try {
      const { data } = await supabase
        .from('investor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        // Was an inline mapping that dropped the thesis, every social link and
        // the expertise areas (hardcoded `[]`) — so fields that saved
        // correctly still vanished on the next sign-in. Same fix, and same
        // reasoning, as mapStartupProfileRow. Locked by profile-mappers tests.
        store.setInvestorProfile(mapInvestorProfileRow(data));
      }
    } catch (err) {
      reportError(err, { context: 'syncUserToProfileStore.investorProfile', silent: true });
    }
  } else if (role === 'startup' && !store.startupProfile) {
    try {
      // Resolve via the membership seam, NOT `.eq('user_id', user.id)` — that
      // column is NULL for create-path founders, so the old lookup hydrated
      // nothing for them and the store stayed empty after every sign-out →
      // sign-in (the sign-out cascade clears it).
      const data = await loadMyStartupProfileRow(supabase);

      if (data) {
        // mapStartupProfileRow also restores the data-room connection fields
        // (data_room_connected / provider / folder_id) that were previously
        // dropped on load — see profile-mappers.ts + its test.
        store.setStartupProfile(mapStartupProfileRow(data));
      }
    } catch (err) {
      reportError(err, { context: 'syncUserToProfileStore.startupProfile', silent: true });
    }
  }
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    profile: null,
    isLoading: true,
    isConfigured: isSupabaseConfigured(),
    isPasswordRecovery: false,
  });

  // Usage-analytics consent follows the ACCOUNT: profiles.analytics_opt_out
  // (Phase 118-08) is the one place the choice lives, and the database enforces
  // it for analytics_events. The tracker stays `pending` (holds events in memory,
  // sends nothing) until this has run for the signed-in user, and sign-out puts
  // it back to `pending` (resetAnalytics). Re-runs whenever the profile is
  // re-read, so a change made in Settings or Manage my data — or on another
  // device, at the next profile read — takes effect here. See analytics/tracker.ts.
  const consentUserId = state.user?.id ?? null;
  const consentProfileId = state.profile?.id ?? null;
  const analyticsOptOut = state.profile?.analytics_opt_out ?? null;
  useEffect(() => {
    if (!consentUserId || consentProfileId !== consentUserId || analyticsOptOut === null) return;
    setAnalyticsConsent(consentUserId, !analyticsOptOut);
  }, [consentUserId, consentProfileId, analyticsOptOut]);

  // Timer that closes the recovery window. Held in a ref so opening a second
  // window (or closing one early) cannot leave an orphaned timer that later
  // clears a NEWER window out from under the user.
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const endPasswordRecovery = useCallback(() => {
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
    setState((prev) => (prev.isPasswordRecovery ? { ...prev, isPasswordRecovery: false } : prev));
  }, []);

  const beginPasswordRecovery = useCallback(() => {
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = setTimeout(() => {
      recoveryTimerRef.current = null;
      setState((prev) => ({ ...prev, isPasswordRecovery: false }));
    }, PASSWORD_RECOVERY_WINDOW_MS);
    setState((prev) => ({ ...prev, isPasswordRecovery: true }));
  }, []);

  // Never leak the timer across unmount.
  useEffect(() => () => {
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
  }, []);

  // Fetch user profile from database
  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    if (!state.isConfigured) return null;

    try {
      // maybeSingle, not single: this function's contract is `Profile | null`, so
      // "no profile row yet" is a legitimate outcome — it happens in the window
      // between auth.users being created and the handle_new_user trigger writing
      // profiles. `.single()` turned that into PGRST116 "Cannot coerce the result
      // to a single JSON object", which was reported to Sentry as a warning on
      // every occurrence (13 events) while the caller quietly returned null
      // anyway. The query now says what the caller already meant.
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        reportError(error, { context: 'fetchProfile', level: 'warning' });
        return null;
      }

      return data;
    } catch (error) {
      reportError(error, { context: 'fetchProfile', level: 'warning', silent: true });
      return null;
    }
  }, [state.isConfigured]);

  // Initialize auth state
  useEffect(() => {
    if (!state.isConfigured) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    // Get initial session (with timeout so the app doesn't stall if Supabase is unreachable)
    let timedOut = false;
    // Cleared once the race settles — an uncleared 8s timer keeps the runtime
    // awake on every app start (and holds the Jest worker open).
    let initTimer: ReturnType<typeof setTimeout> | undefined;
    const initSession = async () => {
      try {
        const { data: { session } } = await Promise.race([
          supabase.auth.getSession(),
          new Promise<never>((_, reject) => {
            initTimer = setTimeout(() => { timedOut = true; reject(new Error('Session initialization timed out')); }, 8000);
          }),
        ]).finally(() => {
          if (initTimer) clearTimeout(initTimer);
        });
        if (timedOut) return;

        // Validate the restored session against the SERVER. getSession() returns
        // the LOCALLY-CACHED token, which can outlive its user — e.g. the account
        // was deleted server-side while the token is still un-expired. Acting on
        // that ghost session makes the router think it's signed in and bounces it
        // in a loop (welcome → onboarding → welcome). getUser() hits the server
        // and errors when the user no longer exists → treat as signed out, clear
        // the stale token, and land cleanly on the welcome screen. (Adi 2026-07-22;
        // routing-trusts-DB-not-cache — see project_profile_routing_review.)
        if (session?.user) {
          const { data: liveUser, error: userErr } = await supabase.auth.getUser();
          if (userErr || !liveUser?.user) {
            // NOT swallowed. A ghost session that fails to clear leaves the
            // stale token in storage, and `.catch(() => {})` meant there was
            // no signal at all when that happened — the user loops
            // welcome → onboarding → welcome and we never hear about it.
            //
            // Deliberately does not rethrow: this is already an error path,
            // and failing here would strand the user on a broken screen
            // rather than landing them cleanly on welcome. The state reset
            // below runs either way.
            try {
              await supabase.auth.signOut();
            } catch (signOutErr) {
              reportError(signOutErr, {
                context: 'AuthProvider.initSession.discardGhostSession',
                level: 'warning',
              });
            }
            setState((prev) => ({
              ...prev,
              session: null,
              user: null,
              profile: null,
              isLoading: false,
            }));
            return;
          }
        }

        let profile: Profile | null = null;
        if (session?.user) {
          profile = await fetchProfile(session.user.id);
        }
        setState((prev) => ({
          ...prev,
          session,
          // Boundary: Supabase hands back a plain `User`. This is where the
        // auth id enters the typed world, so this is where it is branded.
        user: brandSessionUser(session),
          profile,
          isLoading: false,
        }));

        // Sync user info to profile store on initial session load
        if (session?.user) {
          syncUserToProfileStore(session.user, profile);

          // Trigger LinkedIn enrichment for returning users on session restore
          if (profile && !(profile.onboarding_progress as { linkedin_enriched?: boolean } | null)?.linkedin_enriched) {
            const linkedInIdentity = session.user.identities?.find(
              (identity) => identity.provider === 'linkedin_oidc'
            );
            if (linkedInIdentity) {
              const identityData = linkedInIdentity.identity_data || {};
              // Prefer stored linkedin_url (set during onboarding or manually) over OIDC sub
              // LinkedIn OIDC sub is an opaque ID, not the public username
              const storedUrl = useProfileStore.getState().investorProfile?.linkedInUrl;
              const linkedInUrl = storedUrl || identityData.profile_url || null;
              if (linkedInUrl) {
                triggerLinkedInEnrichment(session.user.id, linkedInUrl);
              }
            }
          }
        }
      } catch (err) {
        reportError(err, { context: 'AuthProvider.getSession', level: 'warning', silent: true });
        setState((prev) => ({ ...prev, session: null, user: null, profile: null, isLoading: false }));
      }
    };

    initSession();

    // Listen for auth changes.
    //
    // CRITICAL: This callback is intentionally NOT async at the top level.
    // The Supabase SDK's _notifyAllSubscribers awaits every callback before
    // signInWithPassword resolves (Promise.all on subscriber callbacks).
    // If we await DB queries here (fetchProfile, syncUserToProfileStore)
    // and one of them hangs, the entire signInWithPassword stalls — which
    // is exactly the symptom we hit on inverio.ai (login times out at the
    // outer 20s wrapper despite the underlying /token call returning in
    // <1s). Pattern from Supabase docs: do the sync state update inline,
    // defer all async work with setTimeout(0).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // 1. Synchronous state update — completes before _notifyAllSubscribers
      //    awaits us, so signInWithPassword can resolve immediately.
      //    Profile is filled in asynchronously below.
      setState((prev) => ({
        ...prev,
        session,
        // Boundary: Supabase hands back a plain `User`. This is where the
        // auth id enters the typed world, so this is where it is branded.
        user: brandSessionUser(session),
        profile: session?.user ? prev.profile : null,
        isLoading: false,
      }));

      // Attribute production Sentry errors to the user (id only) across every
      // auth transition — SIGNED_IN, session restore, and SIGNED_OUT (#99).
      setSentryUser(session?.user?.id ?? null);

      // PASSWORD RECOVERY (SEC-AUTH-03). This event is the ONLY thing that
      // opens the recovery window. Supabase emits it exclusively when a real
      // recovery link is consumed (web: detectSessionInUrl parses the
      // `type=recovery` fragment), so it cannot be reached by navigating to
      // /reset-password with an ordinary session — which is precisely the
      // bypass this replaces.
      if (event === 'PASSWORD_RECOVERY') {
        beginPasswordRecovery();
      } else if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
        // Any other identity transition ends recovery: signing out spends it,
        // and a fresh ordinary sign-in must never inherit a window opened
        // before it.
        endPasswordRecovery();
      }

      if (!session?.user) return;

      // 2. Deferred async work — runs after the SDK has notified all
      //    subscribers and signInWithPassword has resolved. The user is
      //    already navigating to the dashboard while this runs.
      setTimeout(() => {
        void (async () => {
          try {
            let profile = await fetchProfile(session.user.id);

            // Handle new LinkedIn OAuth users - create profile if doesn't exist
            if (!profile && (event === 'SIGNED_IN' || event === 'USER_UPDATED') && !profileCreationInProgress) {
              const user = session.user;
              const linkedInIdentity = user.identities?.find(
                (identity) => identity.provider === 'linkedin_oidc'
              );

              if (linkedInIdentity) {
                profileCreationInProgress = true;
                try {
                  const identityData = linkedInIdentity.identity_data || {};
                  const userMetadata = user.user_metadata || {};

                  const linkedInProfileData = {
                    name: userMetadata.full_name || userMetadata.name || identityData.name || null,
                    email: user.email || identityData.email || null,
                    avatarUrl: userMetadata.avatar_url || userMetadata.picture || identityData.picture || null,
                    linkedInUrl: identityData.profile_url ||
                      (identityData.sub ? `https://www.linkedin.com/in/${identityData.sub}` : null),
                  };

                  let pendingRole = useProfileStore.getState().pendingAuthRole;
                  if (!pendingRole && typeof sessionStorage !== 'undefined') {
                    pendingRole = sessionStorage.getItem('pendingAuthRole') as 'startup' | 'investor' | null;
                    if (pendingRole) sessionStorage.removeItem('pendingAuthRole');
                  }
                  useProfileStore.getState().setPendingAuthRole(null);

                  const roleToStore = pendingRole || 'pending';

                  // @ts-ignore - Supabase types mismatch
                  const { error: profileError } = await supabase.from('profiles').upsert({
                    id: user.id,
                    email: user.email!,
                    role: roleToStore,
                    onboarding_completed: false,
                    onboarding_progress: {
                      linkedin_data: linkedInProfileData,
                    },
                  }, { onConflict: 'id' });

                  if (profileError) {
                    reportError(profileError, { context: 'createLinkedInProfile', level: 'error' });
                  } else {
                    profile = await fetchProfile(user.id);

                    // NOTE: We do NOT call supabase.auth.updateUser() here.
                    // updateUser() acquires the GoTrue navigator lock which
                    // serializes with _getAccessToken() → getSession() used
                    // by every PostgREST call.

                    // No placeholder company (118-10): the onboarding company
                    // step creates or claims it, owned through team membership —
                    // never a startup_profiles.user_id row.

                    if (linkedInProfileData.linkedInUrl) {
                      triggerLinkedInEnrichment(user.id, linkedInProfileData.linkedInUrl);
                    }
                  }
                } finally {
                  profileCreationInProgress = false;
                }
              }
            }

            // For returning LinkedIn users: enrich if not yet enriched
            if (profile && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
              const linkedInIdentity = session.user.identities?.find(
                (identity) => identity.provider === 'linkedin_oidc'
              );
              if (linkedInIdentity && !(profile.onboarding_progress as { linkedin_enriched?: boolean } | null)?.linkedin_enriched) {
                const identityData = linkedInIdentity.identity_data || {};
                const linkedInUrl = identityData.profile_url ||
                  (identityData.sub ? `https://www.linkedin.com/in/${identityData.sub}` : null);
                if (linkedInUrl) {
                  triggerLinkedInEnrichment(session.user.id, linkedInUrl);
                }
              }
            }

            // Backfill profile in state once it's loaded
            setState((prev) => ({ ...prev, profile }));

            // Sync authenticated user info to the profile store so dashboard
            // components always have name/avatar available.
            syncUserToProfileStore(session.user, profile);
          } catch (err) {
            reportError(err, { context: 'onAuthStateChange.deferred', level: 'warning', silent: true });
          }
        })();
      }, 0);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [state.isConfigured, fetchProfile, beginPasswordRecovery, endPasswordRecovery]);

  // Native token auto-refresh, driven by AppState.
  //
  // The Supabase client has autoRefreshToken: true, but on React Native the JS
  // runtime is suspended while the app is backgrounded, so the refresh timer
  // stalls and the access token can silently expire — the user still looks
  // logged in, but every PostgREST/edge call then 401s. Supabase's documented
  // native pattern is to control the refresher off AppState: start it on
  // 'active' (which also refreshes immediately if the token expired while
  // backgrounded) and stop it otherwise. Web is excluded — the browser keeps the
  // tab's refresh timer alive — and mock mode has no real session to refresh.
  useEffect(() => {
    if (isWeb || mode.isMock) return;

    if (AppState.currentState === 'active') {
      supabase.auth.startAutoRefresh();
    }
    const sub = AppState.addEventListener('change', (next) => {
      if (next === 'active') {
        supabase.auth.startAutoRefresh();
      } else {
        supabase.auth.stopAutoRefresh();
      }
    });
    return () => {
      sub.remove();
      supabase.auth.stopAutoRefresh();
    };
  }, []);

  // Sign up with email/password
  const signUp = useCallback(
    async (email: string, password: string, role: UserRole, firstName?: string, lastName?: string) => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError, needsEmailConfirmation: false };
      }

      // Pre-check: invite-only beta — verify the email has been invited.
      // S3: uses the check_waitlist_status RPC (SECURITY DEFINER, exact-email, status-only)
      // instead of selecting from the waitlist table — the old anon SELECT policy leaked the
      // full lead table. See migration 20260703120000_security_audit_rls_hardening.sql.
      if (isBetaMode()) {
        const { data: waitlistStatus, error: waitlistError } = await rpc('check_waitlist_status', {
          p_email: email.toLowerCase(),
        });

        // FAIL OPEN. The error used to be discarded, so a failed lookup was
        // indistinguishable from "this email is not on the waitlist" — and when
        // `check_waitlist_status` lost its EXECUTE grant to `anon` in the RPC
        // privilege lockdown, every call returned 42501 and EVERY signup was
        // refused as uninvited, invited users included, for ten days with
        // nothing in Sentry. This pre-check is advisory UX, not a security
        // boundary (nothing server-side gates signup on the waitlist), so an
        // infrastructure failure must never be the thing that denies a
        // legitimate user an account.
        if (waitlistError) {
          reportError(waitlistError, { context: 'AuthProvider.signUp.waitlistPrecheck' });
        } else if (!waitlistStatus) {
          return {
            error: { message: 'Registration is invite-only during the beta. Please join the waitlist at inverio.ai first.' } as AuthError,
            needsEmailConfirmation: false,
          };
        } else if (waitlistStatus !== 'invited') {
          return {
            error: { message: "You're on the waitlist but haven't been invited yet. We'll email you when it's your turn!" } as AuthError,
            needsEmailConfirmation: false,
          };
        }
      }

      const { data, error } = await withTimeoutHelper(
        supabase.auth.signUp({
          email,
          password,
          options: {
            // first_name/last_name flow into profiles via the handle_new_user
            // trigger, so onboarding step 1 prefills the founder's name.
            data: {
              role,
              first_name: firstName?.trim() || undefined,
              last_name: lastName?.trim() || undefined,
            },
          },
        }),
        10000,
        'Sign-up timed out. Please check your connection and try again.',
      );

      if (!error && data.user) {
        identify(data.user.id);
        track('sign_up', { role, method: 'email' });

        // Profile creation is handled by the `on_auth_user_created` DB trigger
        // (handle_new_user), NOT here: under the email-confirmation gate signUp()
        // returns no session, so a client-side `profiles.insert` runs anonymously
        // and is rejected by RLS (auth.uid() = id) — which stranded every real
        // email signup with no profile. The trigger creates it from the signup
        // metadata role regardless of session/confirmation.
        // The role-specific startup_profiles row is intentionally NOT created here
        // either — the onboarding flow (search-first company → create/claim) owns
        // that, so a `stage='pre-seed'` skeleton would reintroduce the silent
        // default + user_id-ownership the redesign removed.

        // Check if email confirmation is required (session will be null)
        if (!data.session) {
          // Try to sign in immediately (works if email confirmation is disabled)
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (signInError) {
            // If sign-in fails, email confirmation is truly required
            return { error: null, needsEmailConfirmation: true };
          }

          if (signInData.session) {
            return { error: null, needsEmailConfirmation: false };
          }

          return { error: null, needsEmailConfirmation: true };
        }
      }

      return { error, needsEmailConfirmation: false };
    },
    [state.isConfigured]
  );

  // Sign in with email/password (with MFA support)
  const signIn = useCallback(
    async (email: string, password: string) => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError };
      }

      // 8s outer ceiling. The underlying /token call returns in <1s on a
      // healthy connection, so 8s is enough headroom for slow networks
      // without forcing the user to wait an eternity when something is
      // actually wrong. The historical 20s was a workaround for the
      // onAuthStateChange listener blocking _notifyAllSubscribers; that
      // root cause is fixed (listener defers async work) so the wrapper
      // only needs to cover real network slowness.
      const { data, error } = await withTimeoutHelper(
        supabase.auth.signInWithPassword({ email, password }),
        8000,
        'Sign-in timed out. Please check your connection and try again.',
      );

      // Check if MFA is required.
      //
      // listFactors() is bounded by a 3s timeout because it's been observed
      // to occasionally hang on Supabase's side. SEC-AUTH-02 / RM-T1-02:
      // we fail CLOSED on timeout or any error — returning { error } instead
      // of silently treating it as "no MFA needed". Telemetry (identify +
      // track('sign_in')) is deferred until we have a confirmed terminal
      // state so a fail-closed bail-out doesn't pollute analytics with a
      // successful sign-in the user can't actually use.
      if (data?.user && !error) {
        // A real sign-in must clear any lingering "Try Demo" bypass so a real
        // authenticated session can never inherit sample-data mode. (Sign-out
        // already clears it; this covers Try-Demo → real-login without sign-out.)
        deactivateDemoBypass();

        let verifiedFactors: { id: string; status: string }[] = [];
        try {
          const { data: mfaData } = await withTimeoutHelper(
            supabase.auth.mfa.listFactors(),
            3000,
            'mfa_list_timeout',
          );
          verifiedFactors = mfaData?.totp?.filter((f) => f.status === 'verified') ?? [];
        } catch (err) {
          // FAIL CLOSED. A timeout or thrown error here previously dropped the
          // user past MFA enforcement — that is the SEC-AUTH-02 defect. We now
          // surface the failure to the caller and let the user retry.
          reportError(err, { context: 'signIn.mfa.listFactors', level: 'error' });
          // ...and DESTROY the provisional session (SEC-AUTH-02b). Returning an
          // error only stops the UI advancing; the AAL1 session that
          // signInWithPassword already persisted is a real, usable credential
          // that survives an app restart. Leaving it behind turns "MFA
          // required" into "MFA skipped" for anyone who can make this lookup
          // fail. See discardProvisionalSession.
          await discardProvisionalSession('signIn.mfa.listFactors');
          return {
            error: {
              message: 'MFA factor lookup unavailable. Please retry sign-in.',
            } as AuthError,
          };
        }

        if (verifiedFactors.length > 0) {
          // MFA is required — create a challenge (also bounded). Telemetry
          // fires after `mfa.challenge` resolves successfully so a failed
          // challenge doesn't show up as a successful sign-in either.
          const factor = verifiedFactors[0];
          try {
            const { data: challengeData, error: challengeError } = await withTimeoutHelper(
              supabase.auth.mfa.challenge({ factorId: factor.id }),
              5000,
              'mfa_challenge_timeout',
            );

            if (challengeError) {
              await discardProvisionalSession('signIn.mfa.challenge');
              return { error: challengeError };
            }

            identify(data.user.id);
            track('sign_in', { method: 'email' });

            return {
              error: null,
              mfaRequired: true,
              mfaChallenge: {
                factorId: factor.id,
                challengeId: challengeData.id,
              },
            };
          } catch (err) {
            reportError(err, { context: 'signIn.mfa.challenge', level: 'error' });
            await discardProvisionalSession('signIn.mfa.challenge');
            return {
              error: { message: 'MFA challenge failed. Please try again.' } as AuthError,
            };
          }
        }

        // No MFA factor enrolled — terminal success. Fire telemetry now.
        identify(data.user.id);
        track('sign_in', { method: 'email' });
      }

      return { error };
    },
    [state.isConfigured]
  );

  // Sign in with LinkedIn OAuth (platform-aware)
  const signInWithLinkedIn = useCallback(async () => {
    if (!state.isConfigured) {
      return { error: { message: 'Supabase not configured' } as AuthError };
    }

    // Web: use redirect-based OAuth flow
    if (Platform.OS === 'web') {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
          scopes: 'openid profile email',
        },
      });
      return { error };
    }

    // Mobile: use in-app browser with skipBrowserRedirect
    try {
      const redirectUri = makeRedirectUri({ scheme: 'inverio', path: 'auth/callback' });

      const { data, error } = await withTimeoutHelper(
        supabase.auth.signInWithOAuth({
          provider: 'linkedin_oidc',
          options: {
            skipBrowserRedirect: true,
            redirectTo: redirectUri,
            scopes: 'openid profile email',
          },
        }),
        10000,
        'LinkedIn sign-in timed out. Please check your connection and try again.',
      );

      if (error || !data?.url) {
        return { error: error ?? { message: 'Failed to get OAuth URL' } as AuthError };
      }

      // Open the OAuth URL in an in-app browser (SFSafariViewController / Chrome Custom Tab)
      const result = await WebBrowser.openAuthSessionAsync(data.url, redirectUri);

      if (result.type !== 'success' || !result.url) {
        // User cancelled or dismissed the browser — not an error to surface
        return { error: null };
      }

      // Extract tokens from the redirect URL fragment
      const url = new URL(result.url);
      // Supabase puts tokens in the hash fragment: #access_token=...&refresh_token=...
      const hashParams = new URLSearchParams(url.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (accessToken && refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        return { error: sessionError };
      }

      // Fallback: tokens might be in query params for some configurations
      const code = url.searchParams.get('code');
      if (code) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        return { error: exchangeError };
      }

      return { error: { message: 'No authentication tokens received' } as AuthError };
    } catch (err: any) {
      reportError(err, { context: 'signInWithLinkedIn.mobile' });
      return { error: { message: err.message || 'LinkedIn sign-in failed' } as AuthError };
    }
  }, [state.isConfigured]);

  // Sign out — single source of truth: onAuthStateChange listener clears auth state.
  // signOut only resets app-level stores and triggers the Supabase sign-out which
  // fires the listener. No competing setState calls.
  const signOut = useCallback(async () => {
    // Stop tracking the signed-out user: clear analytics identity + queued events.
    // We deliberately do NOT flush on sign-out — once the session is revoked (and
    // on web the sb-* localStorage keys are purged below) any insert runs as an
    // anonymous client and is rejected by the analytics_events RLS policy (42501).
    // Pending events are best-effort; the 10s flush timer drains them mid-session.
    resetAnalytics();

    // Re-arm the LinkedIn enrichment dedup guard — otherwise it stays tripped and
    // the next user to sign in on this device never gets enriched.
    resetEnrichmentGuard();

    // 1. Reset app-level stores immediately — instant UX feedback.
    //    useInverioStore.reset() cascades to every persisted store, including
    //    the Drive/Dropbox connection STATE. (Their OAuth tokens live in
    //    SecureStore, not the store — purged separately in step 1a.)
    queryClient.clear();
    useInverioStore.getState().reset();
    useProfileStore.getState().reset();
    useSubscriptionStore.getState().reset();
    useScoreHistoryStore.getState().reset();

    // 1a. Purge cloud-storage OAuth tokens from SecureStore — UNCONDITIONAL
    //     (not gated on an active Supabase session). Store reset() cannot reach
    //     SecureStore, so without this the next user on the device would re-read
    //     a still-valid token and silently reconnect to the previous user's
    //     Drive/Dropbox. Fire-and-forget: local deletes, never block sign-out.
    clearGoogleTokens().catch((err) => reportError(err, { context: 'AuthProvider.signOut.clearGoogleTokens', silent: true }));
    clearDropboxTokens().catch((err) => reportError(err, { context: 'AuthProvider.signOut.clearDropboxTokens', silent: true }));

    // 2. Deactivate demo bypass so auth guards don't keep passing
    deactivateDemoBypass();

    // 2a. Close any open password-recovery window. Signing out must spend the
    //     capability, not leave it live for the next person on the device.
    endPasswordRecovery();

    // 3. Force-clear auth state eagerly — don't wait for onAuthStateChange listener
    //    which fires asynchronously and can race with navigation
    setState((prev) => ({
      ...prev,
      session: null,
      user: null,
      profile: null,
      isLoading: false,
    }));

    // 4. Clear Supabase localStorage keys (web)
    if (isWeb && typeof window !== 'undefined' && window.localStorage) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && (key.includes('supabase') || key.includes('sb-'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => window.localStorage.removeItem(key));
    }

    // 5. Revoke the Supabase session server-side — UNCONDITIONALLY, and AWAITED.
    //
    //    Unconditional: this MUST NOT be gated on `state.session`. signOut is
    //    memoized with useCallback([queryClient]), and because the React Query
    //    client is a stable reference the closure freezes at first render, when
    //    state.session is still null (getSession has not resolved). A
    //    `if (state.session)` gate therefore reads a permanently-null value and
    //    never fires — leaving the live GoTrue session alive to re-emit via
    //    onAuthStateChange and silently re-authenticate the user.
    //    supabase.auth.signOut() is a safe no-op when no session exists.
    //
    //    Awaited (SEC-AUTH-05): this was previously fire-and-forget, so signOut
    //    resolved — and the UI navigated to a signed-out state — while the
    //    revocation was still in flight, and a FAILED revocation was invisible.
    //    Local state was clean; the server session was not. We now await the
    //    result and report it.
    //
    //    Bounded: an unbounded await would let a hung network produce a logout
    //    the user can never complete. On timeout we proceed — local cleanup has
    //    already happened above, so the user is signed out on this device
    //    regardless; only the server-side revocation is unconfirmed.
    let remoteRevoked = false;
    try {
      const { error } = await withTimeoutHelper(
        supabase.auth.signOut({ scope: 'global' }),
        SIGN_OUT_REVOCATION_TIMEOUT_MS,
        'sign_out_revocation_timeout',
      );
      // A returned AuthError is NOT success — treat it exactly like a throw.
      remoteRevoked = !error;
      if (error) {
        reportError(error, { context: 'AuthProvider.signOut.revocation', level: 'warning', silent: true });
      }
    } catch (err) {
      // Network failure or timeout. Never rethrow: local sign-out has already
      // completed and the caller must not be left in a half-signed-out UI.
      reportError(err, { context: 'AuthProvider.signOut.revocation', level: 'warning', silent: true });
    }

    // Caller-visible outcome. Deliberately a bare boolean — no token, no error
    // payload, nothing that could be rendered or logged into a support ticket.
    return { remoteRevoked };
  }, [queryClient, endPasswordRecovery]);

  // Refresh profile data
  const refreshProfile = useCallback(async () => {
    if (!state.user) return;
    const profile = await fetchProfile(state.user.id);
    setState((prev) => ({ ...prev, profile }));

    // Keep the store's display name in sync with the freshly-fetched profile,
    // so a name edit (which writes profiles.first_name/last_name then calls
    // refreshProfile) surfaces immediately — without waiting for the next full
    // login/sync. setUser replaces wholesale, so spread the current user.
    const store = useProfileStore.getState();
    const current = store.user;
    if (current) {
      const name = deriveDisplayName(profile, state.user);
      if (name !== current.name) store.setUser({ ...current, name });
    }
  }, [state.user, fetchProfile]);

  // Get LinkedIn data from user identities
  const getLinkedInData = useCallback((): LinkedInData => {
    if (!state.user) {
      return {
        isLinkedInAuth: false,
        linkedInUrl: null,
        linkedInId: null,
        name: null,
        email: null,
        avatarUrl: null,
      };
    }

    // Check user identities for LinkedIn provider
    const identities = state.user.identities ?? [];
    const linkedInIdentity = identities.find(
      (identity) => identity.provider === 'linkedin_oidc'
    );

    if (!linkedInIdentity) {
      return {
        isLinkedInAuth: false,
        linkedInUrl: null,
        linkedInId: null,
        name: null,
        email: null,
        avatarUrl: null,
      };
    }

    // Extract LinkedIn profile data
    const identityData = linkedInIdentity.identity_data || {};
    const userMetadata = state.user.user_metadata || {};

    // Extract LinkedIn ID and construct profile URL
    const linkedInId = identityData.sub ?? null;
    const linkedInUrl = identityData.profile_url ||
      (linkedInId ? `https://www.linkedin.com/in/${linkedInId}` : null);

    // Extract name, email, avatar
    const name = userMetadata.full_name || userMetadata.name || identityData.name || null;
    const email = state.user.email || identityData.email || null;
    const avatarUrl = userMetadata.avatar_url || userMetadata.picture || identityData.picture || null;

    return {
      isLinkedInAuth: true,
      linkedInUrl,
      linkedInId,
      name,
      email,
      avatarUrl,
    };
  }, [state.user]);

  // Password Recovery - Send reset email
  //
  // Routes through `auth-password-reset` edge function (Phase 113 / ISS-113-04-A)
  // which applies an IP-keyed rate limit (3/hour) via the shared
  // _shared/metering/rate-limit.ts helper. The edge function proxies to
  // `supabase.auth.resetPasswordForEmail` server-side, preserving Supabase Auth's
  // existing email-enumeration protections (always-returns-success).
  const resetPassword = useCallback(
    async (email: string) => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError };
      }

      // Universal Link (not the custom `inverio://` scheme): an https URL on the
      // app's associated domain opens the app if installed (Universal Links /
      // App Links) and falls back to the website reset page otherwise. Requires
      // the associatedDomains entitlement + the hosted
      // /.well-known/apple-app-site-association & assetlinks.json (this PR), and
      // activates on the next native build. www.inverio.ai/** is already in the
      // Supabase redirect allow-list. Native is domain-bound to prod (Universal
      // Links can't target a per-env host); web uses the current origin.
      const redirectTo = Platform.OS === 'web'
        ? (typeof window !== 'undefined' ? `${window.location.origin}/reset-password` : undefined)
        : 'https://www.inverio.ai/reset-password';

      // Direct fetch instead of supabase.functions.invoke to keep this lib
      // dependency-free (callEdgeFunction lives in edge-functions.ts and
      // imports app-mode, which would create a circular import here).
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const supabaseUrl = (supabase as unknown as { supabaseUrl?: string }).supabaseUrl
          ?? process.env.EXPO_PUBLIC_SUPABASE_URL;
        const anonKey = (supabase as unknown as { supabaseKey?: string }).supabaseKey
          ?? process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

        const res = await fetch(`${supabaseUrl}/functions/v1/auth-password-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token ?? anonKey}`,
            'apikey': anonKey ?? '',
          },
          body: JSON.stringify({ email, redirectTo }),
        });

        if (res.status === 429) {
          return { error: { message: 'Too many reset requests. Please try again later.' } as AuthError };
        }
        if (!res.ok) {
          return { error: { message: 'Failed to send reset email' } as AuthError };
        }
        return { error: null };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send reset email';
        return { error: { message } as AuthError };
      }
    },
    [state.isConfigured]
  );

  // Password Recovery - Update password (after clicking reset link)
  const updatePassword = useCallback(
    async (newPassword: string) => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError };
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      return { error };
    },
    [state.isConfigured]
  );

  // Email confirmation — resend the Supabase "confirm signup" email.
  //
  // Uses the SDK's built-in resend (which respects Supabase Auth's own rate
  // limiting and email-enumeration protections). `emailRedirectTo` sends the user
  // back into the app after they click the link; the existing session then reports
  // `email_confirmed_at`, which flips `emailConfirmed` true and clears the gate.
  const resendConfirmationEmail = useCallback(
    async (email: string) => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError };
      }

      const emailRedirectTo = Platform.OS === 'web'
        ? (typeof window !== 'undefined' ? window.location.origin : undefined)
        : 'inverio://';

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo },
      });

      return { error };
    },
    [state.isConfigured]
  );

  // Email confirmation — change the pending email address. Supabase sends a
  // confirmation email to the NEW address; `email_confirmed_at` stays null until
  // it's confirmed, so the access gate remains in effect until then.
  const changeEmail = useCallback(
    async (newEmail: string) => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError };
      }

      const { error } = await supabase.auth.updateUser({ email: newEmail });

      return { error };
    },
    [state.isConfigured]
  );

  // Helper to run operation with timeout
  const withTimeout = <T,>(promise: Promise<T>, ms: number, errorMsg: string): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(errorMsg)), ms)
      )
    ]);
  };

  // MFA - Enroll a new TOTP factor (uses Edge Function via direct fetch)
  const enrollMFA = useCallback(async () => {
    if (!state.isConfigured) {
      return { error: { message: 'Supabase not configured' } as AuthError };
    }

    const friendlyName = `Inverio-${Date.now()}`;
    const result = await callMfaEdgeFunction('enroll', { friendlyName });

    if (!result.success) {
      return { error: { message: result.error || 'Failed to enroll MFA' } as AuthError };
    }

    return {
      error: null,
      qrCode: result.data?.qrCode,
      secret: result.data?.secret,
      factorId: result.data?.factorId,
    };
  }, [state.isConfigured]);

  // MFA - Verify enrollment with TOTP code (uses Edge Function)
  const verifyMFAEnrollment = useCallback(
    async (factorId: string, code: string): Promise<{ error: AuthError | null; success: boolean }> => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError, success: false };
      }

      try {
        const result = await callMfaFunction('enroll-verify', factorId, code);

        if (!result.success) {
          return { error: { message: result.error || 'Failed to verify MFA' } as AuthError, success: false };
        }

        // Try to refresh session but don't block on it
        supabase.auth.refreshSession().catch((err) => reportError(err, { context: 'verifyMFAEnrollment.refreshSession', silent: true }));

        return { error: null, success: true };
      } catch (err: any) {
        return { error: { message: err.message || 'Failed to verify MFA' } as AuthError, success: false };
      }
    },
    [state.isConfigured]
  );

  // MFA - Verify during login
  const verifyMFA = useCallback(
    async (factorId: string, challengeId: string, code: string) => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError };
      }

      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code,
      });

      return { error };
    },
    [state.isConfigured]
  );

  // MFA - Verify active factor to elevate to AAL2 (uses Edge Function)
  const verifyActiveMFA = useCallback(
    async (factorId: string, code: string): Promise<{ error: AuthError | null; success: boolean }> => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError, success: false };
      }

      try {
        const result = await callMfaFunction('verify', factorId, code);

        if (!result.success) {
          return { error: { message: result.error || 'Failed to verify MFA' } as AuthError, success: false };
        }

        // Refresh session to get updated AAL level
        await supabase.auth.refreshSession();

        return { error: null, success: true };
      } catch (err: any) {
        return { error: { message: err.message || 'Failed to verify MFA' } as AuthError, success: false };
      }
    },
    [state.isConfigured]
  );

  // MFA - Remove a factor (uses Edge Function when code is provided for verified factors)
  const unenrollMFA = useCallback(
    async (factorId: string, code?: string): Promise<{ error: AuthError | null; success: boolean }> => {
      if (!state.isConfigured) {
        return { error: { message: 'Supabase not configured' } as AuthError, success: false };
      }

      // If code is provided, use edge function (for verified factors that require AAL2)
      if (code) {
        try {
          const result = await callMfaFunction('unenroll', factorId, code);

          if (!result.success) {
            return { error: { message: result.error || 'Failed to unenroll MFA' } as AuthError, success: false };
          }

          // Try to refresh session but don't block on it
          supabase.auth.refreshSession().catch((err) => reportError(err, { context: 'unenrollMFA.refreshSession', silent: true }));

          return { error: null, success: true };
        } catch (err: any) {
          return { error: { message: err.message || 'Failed to unenroll MFA' } as AuthError, success: false };
        }
      }

      // No code provided - use direct client call (for unverified factors)
      const { error } = await supabase.auth.mfa.unenroll({
        factorId,
      });

      return { error, success: !error };
    },
    [state.isConfigured]
  );

  // MFA - Get enrolled factors (uses Edge Function via direct fetch)
  const getMFAFactors = useCallback(async () => {
    if (!state.isConfigured) {
      return { factors: [], error: { message: 'Supabase not configured' } as AuthError };
    }

    const result = await callMfaEdgeFunction('list-factors', {});

    if (!result.success) {
      return { factors: [], error: { message: result.error || 'Failed to get MFA factors' } as AuthError };
    }

    return { factors: result.data?.factors || [], error: null }
  }, [state.isConfigured]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        // Derived from the auth user object (arrives with the session — no extra
        // async). null email_confirmed_at → false gates; set → true stays inert.
        emailConfirmed: !!state.user?.email_confirmed_at,
        signUp,
        signIn,
        signInWithLinkedIn,
        signOut,
        refreshProfile,
        getLinkedInData,
        resetPassword,
        updatePassword,
        endPasswordRecovery,
        resendConfirmationEmail,
        changeEmail,
        enrollMFA,
        verifyMFAEnrollment,
        verifyMFA,
        verifyActiveMFA,
        unenrollMFA,
        getMFAFactors,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth state and methods
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated() {
  const { user, isLoading } = useAuth();
  return { isAuthenticated: !!user, isLoading };
}

/**
 * Hook to require authentication - throws if not authenticated
 */
export function useRequireAuth() {
  const { user, isLoading, profile } = useAuth();

  if (!isLoading && !user) {
    throw new Error('User must be authenticated');
  }

  return { user: user!, profile, isLoading };
}
